// ============================================
// components/friends/AddFriend.js
// ============================================
import React, { useState } from 'react';
import { Box, Button, TextField, CircularProgress, Stack, Collapse, Fade } from '@mui/material';
import { isValidEmail } from '../../utils/helpers';

const AddFriend = ({ onAddFriend, disabled, defaultOpen = false }) => {
  const [showInput, setShowInput] = useState(defaultOpen);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');

  const handleSubmit = async () => {
    // Basic validations
    const trimmed = email.trim();
    const tooLong = trimmed.length > 512; // generous hard cap
    const invalid = !trimmed || !isValidEmail(trimmed);
    if (tooLong) {
      setEmailError('Email too long');
      return;
    }
    if (invalid) {
      setEmailError("Please enter a valid email");
      return;
    }
    setEmailError('');

    setLoading(true);
    const result = await onAddFriend(trimmed);
    setLoading(false);
    
    if (result.success) {
      setEmail('');
    }
  };

  const handleCancel = () => {
    setShowInput(false);
    setEmail('');
  };

  if (!showInput) {
    return (
      <Button
        variant="outlined"
        onClick={() => setShowInput(true)}
        disabled={disabled}
        sx={{
          borderRadius: 2,
          fontWeight: 600,
          minWidth: 140,
        }}
      >
        Add Friend
      </Button>
    );
  }

  const hasText = email.trim().length > 0;

  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        size="small"
        label="Friend's email"
        value={email}
        onChange={(e) => {
          const val = e.target.value;
          setEmail(val);
          // Live validation for visibility in tests
          if (val.length > 512) {
            setEmailError('Email too long');
          } else if (val && !isValidEmail(val)) {
            setEmailError('Please enter a valid email');
          } else {
            setEmailError('');
          }
        }}
        type="email"
        inputMode="email"
        autoComplete="email"
        disabled={loading || disabled}
        onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
        sx={{ width: '100%', mb: hasText ? 1 : 0 }}
        inputProps={{ style: { fontSize: '1rem' }, 'data-testid': 'add-friend-input', 'aria-invalid': !!emailError }}
        error={!!emailError}
        helperText={emailError || ' '}
      />
      <Collapse in={hasText} timeout={200} unmountOnExit>
        <Fade in={hasText} timeout={250} unmountOnExit>
          <Stack spacing={1} sx={{ width: '100%' }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || disabled}
              sx={{ borderRadius: 2, fontWeight: 600 }}
              data-testid="add-friend-button"
              fullWidth
            >
              {loading ? <CircularProgress size={18} color="inherit" /> : 'Send Request'}
            </Button>
            <Button
              variant="text"
              onClick={handleCancel}
              disabled={loading}
              sx={{ borderRadius: 2, fontWeight: 600 }}
              fullWidth
            >
              Cancel
            </Button>
          </Stack>
        </Fade>
      </Collapse>
    </Box>
  );
};

export default AddFriend;